import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PasswordInput } from "@/components/ui/password-input";
import { Button, buttonVariants } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { resetPassword } from "@/lib/api";
import { cn } from "@/lib/utils";
import { AuthLayout, AuthNotice, BackToSignIn, PanelBlock, APP_NAME } from "@/components/auth/AuthLayout";

const MIN_LENGTH = 6;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  // New tenant owners arrive here from their onboarding invite email
  const isInvite = searchParams.get("invite") === "1";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  useEffect(() => {
    document.title = `${isInvite ? "Set your password" : "Reset password"} | ${APP_NAME}`;
    return () => { document.title = APP_NAME; };
  }, [isInvite]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setMessage("Invalid or missing reset link. Please request a new one.");
      setStatus("error");
      return;
    }
    if (newPassword.length < MIN_LENGTH) {
      setMessage(`Password must be at least ${MIN_LENGTH} characters.`);
      setStatus("error");
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage("Passwords do not match.");
      setStatus("error");
      return;
    }

    setStatus("loading");
    setMessage("");
    try {
      await resetPassword(token, newPassword);
      setStatus("success");
      setMessage(isInvite
        ? "Your password is set. You can now sign in."
        : "Your password has been reset. You can sign in with your new password.");
    } catch (err: any) {
      setStatus("error");
      setMessage(err?.message || "Reset failed. The link may have expired.");
    }
  };

  const signInLink = (
    <Link to="/login" className={cn(buttonVariants(), "w-full h-11")}>Sign in</Link>
  );

  if (!token) {
    return (
      <AuthLayout title="This link doesn't work" back={<BackToSignIn />}>
        <div className="space-y-4">
          <AuthNotice tone="error">The reset link is invalid or incomplete. Use the link from your email, or request a new one.</AuthNotice>
          <Link to="/forgot-password" className={cn(buttonVariants({ variant: "outline" }), "w-full h-11")}>
            Request a new link
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Say so as soon as the two differ, not only after pressing the button
  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  return (
    <AuthLayout
      title={isInvite ? "Welcome — set your password" : "Set a new password"}
      subtitle={isInvite ? "Choose a password to finish setting up your account." : "Choose a password you don't use anywhere else."}
      back={status === "success" ? undefined : <BackToSignIn />}
      panel={isInvite ? (
        <PanelBlock eyebrow="Welcome" title="Your clinic is ready.">
          <p>One step left: choose your password, then sign in.</p>
          <p>The Dashboard then shows a short setup checklist — clinic address, doctors, fees, your first patient — each step linking to the right screen.</p>
        </PanelBlock>
      ) : (
        <PanelBlock title="Set a new password.">
          <p>Once it's saved, sign in with your new password.</p>
        </PanelBlock>
      )}
    >
      {status === "success" ? (
        <div className="space-y-4">
          <AuthNotice tone="success">{message}</AuthNotice>
          {signInLink}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="newPassword">New password</Label>
            <PasswordInput
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={status === "loading"}
              minLength={MIN_LENGTH}
              required
              autoFocus
              autoComplete="new-password"
              aria-describedby="newPassword-hint"
              className="h-11"
            />
            <p id="newPassword-hint" className="text-xs text-muted-foreground">At least {MIN_LENGTH} characters.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirmPassword">Confirm password</Label>
            <PasswordInput
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={status === "loading"}
              minLength={MIN_LENGTH}
              required
              autoComplete="new-password"
              aria-invalid={mismatch}
              aria-describedby={mismatch ? "confirm-hint" : undefined}
              className="h-11"
            />
            {mismatch && <p id="confirm-hint" className="text-xs text-destructive">Passwords don't match yet.</p>}
          </div>

          {status === "error" && message && <AuthNotice tone="error">{message}</AuthNotice>}

          <Button type="submit" disabled={status === "loading"} className="w-full h-11">
            {status === "loading"
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{isInvite ? "Saving…" : "Resetting…"}</>
              : isInvite ? "Set password" : "Reset password"}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
